'use client'

import React from 'react'
import {Modal} from './Modal'
import {EditorActionBar} from '@fitro/customer-ui/EditorActionBar'
import {Typography} from '@fitro/customer-ui/Typography'
import {Button} from '@fitro/customer-ui/Button'
import {cn} from '../../../lib/utils'
import {isPenPointer, suppressNextCompatibleClick} from '../../../lib/pointer-events'

const PEN_TAP_MOVE_TOLERANCE_PX = 8

type PenActionTap = {
    action: 'cancel' | 'confirm'
    pointerId: number
    clientX: number
    clientY: number
}

interface ConfirmDialogProps {
    isOpen: boolean
    onClose: () => void
    onConfirm: () => void | Promise<void>
    title: string
    description?: React.ReactNode
    children?: React.ReactNode
    confirmLabel?: string
    cancelLabel?: string
    hideCancel?: boolean
    variant?: 'danger' | 'primary'
    confirmDisabled?: boolean
    confirmLoading?: boolean
    cancelDisabled?: boolean
    modalSize?: 'small' | 'medium' | 'large'
    modalClassName?: string
    contentClassName?: string
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
                                                                isOpen,
                                                                onClose,
                                                                onConfirm,
                                                                title,
                                                                description,
                                                                children,
                                                                confirmLabel = 'Confirm',
                                                                cancelLabel = 'Cancel',
                                                                hideCancel = false,
                                                                variant = 'primary',
                                                                confirmDisabled = false,
                                                                confirmLoading = false,
                                                                cancelDisabled = false,
                                                                modalSize = 'small',
                                                                modalClassName,
                                                                contentClassName
}) => {
    const suppressNextActionClickRef = React.useRef<'cancel' | 'confirm' | null>(null)
    const suppressActionClickTimeoutRef = React.useRef<number | null>(null)
    const penActionTapRef = React.useRef<PenActionTap | null>(null)

    const clearActionClickSuppression = React.useCallback(() => {
        suppressNextActionClickRef.current = null
        if (suppressActionClickTimeoutRef.current !== null) {
            window.clearTimeout(suppressActionClickTimeoutRef.current)
            suppressActionClickTimeoutRef.current = null
        }
    }, [])

    const suppressActionClick = React.useCallback((action: 'cancel' | 'confirm') => {
        suppressNextActionClickRef.current = action
        if (suppressActionClickTimeoutRef.current !== null) {
            window.clearTimeout(suppressActionClickTimeoutRef.current)
        }
        suppressActionClickTimeoutRef.current = window.setTimeout(() => {
            if (suppressNextActionClickRef.current === action) {
                suppressNextActionClickRef.current = null
            }
            suppressActionClickTimeoutRef.current = null
        }, 750)
    }, [])

    React.useEffect(() => () => {
        if (suppressActionClickTimeoutRef.current !== null) {
            window.clearTimeout(suppressActionClickTimeoutRef.current)
        }
    }, [])

    const handleActionClick = (action: 'cancel' | 'confirm', handler: () => void | Promise<void>) => {
        if (suppressNextActionClickRef.current === action) {
            clearActionClickSuppression()
            return
        }

        void handler()
    }

    const handleActionPointerDown = (
        event: React.PointerEvent<HTMLButtonElement>,
        action: 'cancel' | 'confirm'
    ) => {
        if (!isPenPointer(event) || event.isPrimary === false || event.button !== 0) {
            penActionTapRef.current = null
            return
        }

        penActionTapRef.current = {
            action,
            pointerId: event.pointerId,
            clientX: event.clientX,
            clientY: event.clientY,
        }
    }

    const handleActionPointerUp = (
        event: React.PointerEvent<HTMLButtonElement>,
        action: 'cancel' | 'confirm',
        handler: () => void | Promise<void>
    ) => {
        const penTap = penActionTapRef.current
        penActionTapRef.current = null
        if (!isPenPointer(event) || event.isPrimary === false || !penTap) return
        if (penTap.action !== action || penTap.pointerId !== event.pointerId) return
        if (Math.hypot(event.clientX - penTap.clientX, event.clientY - penTap.clientY) > PEN_TAP_MOVE_TOLERANCE_PX) return

        event.preventDefault()
        event.stopPropagation()
        suppressNextCompatibleClick(event)
        suppressActionClick(action)
        void handler()
    }

    const clearActionPointerTap = () => {
        penActionTapRef.current = null
    }

    const footer = (
        <EditorActionBar
            secondary={hideCancel ? undefined : (
                <Button
                    type="button"
                    variant="ghost"
                    size="medium"
                    onClick={() => handleActionClick('cancel', onClose)}
                    onPointerDown={(event) => handleActionPointerDown(event, 'cancel')}
                    onPointerUp={(event) => handleActionPointerUp(event, 'cancel', onClose)}
                    onPointerCancel={clearActionPointerTap}
                    onPointerLeave={clearActionPointerTap}
                    disabled={cancelDisabled}
                >
                    {cancelLabel}
                </Button>
            )}
            primary={(
                <Button
                    type="button"
                    variant={variant === 'danger' ? 'error' : 'primary'}
                    size="medium"
                    onClick={() => handleActionClick('confirm', onConfirm)}
                    onPointerDown={(event) => handleActionPointerDown(event, 'confirm')}
                    onPointerUp={(event) => handleActionPointerUp(event, 'confirm', onConfirm)}
                    onPointerCancel={clearActionPointerTap}
                    onPointerLeave={clearActionPointerTap}
                    disabled={confirmDisabled}
                    loading={confirmLoading}
                >
                    {confirmLabel}
                </Button>
            )}
        />
    )

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size={modalSize}
            title={title}
            className={cn(
                'rounded-xl border border-gray-200 dark:border-gray-700',
                modalClassName
            )}
            contentClassName={contentClassName}
            footer={footer}
        >
            <div className="space-y-6">
                {description && (
                    typeof description === 'string' ? (
                        <Typography variant="body" color="muted">
                            {description}
                        </Typography>
                    ) : (
                        <div>{description}</div>
                    )
                )}
                {children}
            </div>
        </Modal>
    )
}

ConfirmDialog.displayName = 'ConfirmDialog'
